import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Linking, Platform } from 'react-native';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import Constants from 'expo-constants';
import { CommonActions, NavigationProp, useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { isNative } from '../../utils/responsive';
import {
  NavigationParamList,
  NotificationRedirectScreenName,
  ScreenName,
  StackName,
} from '../../navigation/types';
import { PostType, PushNotificationData } from '../../client/interfaces';
import { useMessage } from '../Messages/MessageContext';
import { useAuth } from '../AuthContext/AuthContext';
import client from '../../client/client';
import { useNotifications } from '../Notification/NotificationContext';
import { useExtremeEvents } from '../ExtremeEvent/ExtremeEventContext';
import analytics from '../../utils/analytics';

type PushNotificationContextType = {
  pushToken: string;
  pushNotificationsAreEnabled: boolean;
  onNotificationPress: (
    postType: PostType,
    postId: number,
    notificationId: number,
    fromInApp?: boolean,
  ) => Promise<void>;
  registerForPushNotificationsAsync: () => Promise<string>;
  askForPermissionsAgain: () => void;
};

const pushNotificationContextDefaultValues = {
  pushToken: '',
  pushNotificationsAreEnabled: false,
  onNotificationPress: async () => {
    return;
  },
  registerForPushNotificationsAsync: async () => {
    return '';
  },
  askForPermissionsAgain: () => {
    return;
  },
};

const Context = createContext<PushNotificationContextType>(pushNotificationContextDefaultValues);

export const usePushNotifications = () => useContext(Context);

export function PushNotificationProvider({ children }: Readonly<PropsWithChildren>) {
  const [expoPushToken, setExpoPushToken] = useState<string>('');
  const [pushNotificationsAreEnabled, setPushNotificationsAreEnabled] = useState(false);

  const notificationListener = useRef<Notifications.Subscription>(null);
  const responseListener = useRef<Notifications.Subscription>(null);

  const navigation =
    useNavigation<NavigationProp<NavigationParamList, NotificationRedirectScreenName>>();
  const { setMessage } = useMessage();
  const { me, fetchMe } = useAuth();
  const { fetchNotifications } = useNotifications();
  const { fetchExtremeEvents } = useExtremeEvents();
  const { t } = useTranslation();

  const askForPermissionsAgain = () => {
    Alert.alert(t('alerts:ask_for_permission.title'), t('alerts:ask_for_permission.message'), [
      {
        text: t('alerts:ask_for_permission.cancel'),
        style: 'cancel',
      },
      { text: t('alerts:ask_for_permission.ok'), onPress: () => Linking.openSettings() },
    ]);
  };

  // Push értesítés engedélykérés és token kinyerés
  const registerForPushNotificationsAsync = async (): Promise<string> => {
    let token: string;

    // Android config (app.json -> expo.plugins.expo-notifications) felüldefiniálása
    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('default', {
        name: 'default',
        importance: Notifications.AndroidImportance.MAX,
      });
    }

    if (Device.isDevice) {
      const { status: existingStatus } = await Notifications.getPermissionsAsync();
      let finalStatus = existingStatus;
      if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync({
          ios: {
            allowAlert: true,
            allowBadge: true,
            allowSound: true,
          },
        });
        finalStatus = status;
      }
      if (finalStatus !== 'granted') {
        return;
      }

      setPushNotificationsAreEnabled(true);

      token = (
        await Notifications.getExpoPushTokenAsync({
          projectId: Constants.expoConfig.extra.eas.projectId,
        })
      ).data;

      setExpoPushToken(token);
    } else {
      // eslint-disable-next-line no-console
      console.log('A push értesítések csak fizikai eszközökön működnek!');
    }

    return token;
  };

  useEffect(() => {
    if (isNative) {
      Notifications.setNotificationHandler({
        handleNotification: async () => ({
          shouldShowAlert: true,
          shouldShowBanner: true,
          shouldShowList: true,
          shouldPlaySound: true,
          shouldSetBadge: true,
        }),
      });

      registerForPushNotificationsAsync();

      // This listener is fired whenever a notification is received while the app is foregrounded.
      notificationListener.current = Notifications.addNotificationReceivedListener(
        (notification) => {
          fetchNotifications().catch((error) => {
            // eslint-disable-next-line no-console
            console.log({ error, notification });
          });
        },
      );

      // Guards against handling the same response twice: on cold start the
      // response may arrive both from getLastNotificationResponseAsync below and
      // from the listener.
      const handledResponseIds = new Set<string>();

      const handleNotificationResponse = async (response: Notifications.NotificationResponse) => {
        const responseId = response.notification.request.identifier;
        if (handledResponseIds.has(responseId)) {
          return;
        }
        handledResponseIds.add(responseId);

        const decrementBadgeCount = async () => {
          const badgeCount = await Notifications.getBadgeCountAsync();

          if (badgeCount > 0) {
            await Notifications.setBadgeCountAsync(badgeCount - 1);
          }
        };

        const data = response.notification.request.content.data as unknown as PushNotificationData;
        const relatedPost = data?.post;

        if (relatedPost) {
          // Fetch a fresh list to resolve the notification id. On a
          // background-delivered push the new notification isn't in the
          // mount-time `notifications` closure yet, so looking it up there
          // returns undefined and the seen state never gets marked.
          let notificationId: number | undefined;
          try {
            const freshNotifications = await client.getNotifications();
            notificationId = freshNotifications.find((n) => n.postId === relatedPost.id)?.id;
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error?.message ?? error);
          }
          decrementBadgeCount().catch((error) => {
            // eslint-disable-next-line no-console
            console.error(error.message);
          });

          try {
            await analytics().logEvent('push_notification_open', {
              post_type: relatedPost.type,
              notification_title: response.notification.request.content?.title ?? 'unknown',
              notification_body: response.notification.request.content?.body ?? 'unknown',
            });
          } catch (e) {
            console.error(e);
          }

          await onNotificationPress(relatedPost.type, relatedPost.id, notificationId);
        } else {
          // eslint-disable-next-line no-console
          console.log('A push értesítés tartalma nem megfelelő');
        }
      };

      // This listener is fired whenever a user taps on or interacts with a notification while the app is foregrounded or backgrounded.
      responseListener.current = Notifications.addNotificationResponseReceivedListener(
        handleNotificationResponse,
      );

      // Cold start: when the app was killed, the tap that launched it is not
      // delivered to the listener above, so we pull the buffered launch response
      // and route it through the same handler, then clear it so a later normal
      // launch doesn't re-navigate to a stale post.
      Notifications.getLastNotificationResponseAsync()
        .then(async (response) => {
          if (response) {
            await handleNotificationResponse(response);
            await Notifications.clearLastNotificationResponseAsync();
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error?.message ?? error);
        });

      return () => {
        notificationListener.current?.remove();
        responseListener.current?.remove();
      };
    }
  }, []);

  useEffect(() => {
    const createNewPushToken = async (token: string) => {
      await client.createPushToken(token);
      await fetchMe();
    };

    if (expoPushToken && me && !me?.pushTokens?.includes(expoPushToken)) {
      createNewPushToken(expoPushToken).catch((error) => {
        setMessage({ message: error.message, type: 'error' });
      });
    }
  }, [expoPushToken, me]);

  const onExtremeEventNotificationPress = async (id) => {
    await client.deleteExtremeEventSeen(id);
    await fetchExtremeEvents();

    navigation.navigate(ScreenName.SplashScreen);
    setTimeout(() => {
      navigation.navigate(ScreenName.HomeScreen);
    }, 500);
  };

  const onArticleNotificationPress = async (id, fromInApp = false) => {
    // In-app tap, opens article inside the NotificationsStack so back navigation navigates to notificationsScreen
    if (fromInApp) {
      navigation.navigate(ScreenName.ArticleScreen, { postId: id });
      return;
    }
    // Push notification tap, opens article inside the HomeStack so back navigation navigates to homeScreen
    navigation.dispatch(
      CommonActions.navigate({
        name: StackName.HomeStack,
        params: {
          screen: ScreenName.ArticleScreen,
          params: { postId: id },
        },
      }),
    );
  };

  const onEventNotificationPress = async (id, fromInApp = false) => {
    // In-app tap, opens event inside the NotificationsStack so back navigation navigates to notificationsScreen
    if (fromInApp) {
      navigation.navigate(ScreenName.EventScreen, { postId: id });
      return;
    }
    // Push notification tap, opens event inside the HomeStack so back navigation navigates to homeScreen
    navigation.dispatch(
      CommonActions.navigate({
        name: StackName.HomeStack,
        params: {
          screen: ScreenName.EventScreen,
          params: { postId: id },
        },
      }),
    );
  };

  const onBirthdayNotificationPress = async (notificationId) => {
    navigation.navigate(ScreenName.BirthdayScreen, {
      notificationId: notificationId,
    });
  };

  const fallbackNotificationPress = () => {
    navigation.navigate(ScreenName.HomeScreen);
  };

  const onNotificationPress = async (
    postType: PostType,
    postId: number,
    notificationId: number,
    fromInApp = false,
  ): Promise<void> => {
    try {
      // Mark the notification seen for both entry points: the in-app
      // NotificationsScreen tap and the push-notification listener both funnel
      // through here, so the seen state (unread dot) clears in either case.
      if (notificationId) {
        await client.setNotificationsSeen({ ids: [notificationId] });
        await fetchNotifications();
      }

      switch (postType) {
        case PostType.article:
          await onArticleNotificationPress(postId, fromInApp);
          break;
        case PostType.event:
          await onEventNotificationPress(postId, fromInApp);
          break;
        case PostType.extremeEvent:
          await onExtremeEventNotificationPress(postId);
          break;
        case PostType.birthday:
          if (isNative) {
            await onBirthdayNotificationPress(notificationId);
          }
          break;
        default:
          fallbackNotificationPress();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      fallbackNotificationPress();
    }
  };

  const value: PushNotificationContextType = useMemo(
    () => ({
      pushToken: expoPushToken,
      onNotificationPress,
      registerForPushNotificationsAsync,
      pushNotificationsAreEnabled,
      askForPermissionsAgain,
    }),
    [expoPushToken, pushNotificationsAreEnabled],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}
